import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useLocation } from 'react-router';
import { FormApi } from 'final-form';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'CypressId';

import { getCategoryForSticky, STICKY_VRM_EVENTS } from 'Utilities/analytics';
import { vehicleType } from 'Utilities/helpers';
import { applyCypressData } from 'Utilities/index';

import { VRMInput } from 'Containers/app/fastlane/form/vrm/VRMInput';

import Texts from '../../../texts.json';

const T = new MDText(Texts);

const formatVehicleLookupUrl = (value: string) => `/${value.replace(/ /g, '').toUpperCase()}`;

type StickyVrmVersion = 'new' | 'old' | undefined;

export type VrmCheckerProps = {
  analyticsEvents?: {
    invalidVrmWarningDisplayed?: () => void;
    onButtonClick?: Function;
    onInputFocus?: () => void;
    vrmSubmittedSuccessfully?: () => void;
  };
  buttonProps?: {
    className?: string;
    label?: React.ReactNode;
    labelChildren?: React.ReactNode;
    showLabel?: boolean;
  };
  chevron?: boolean;
  className?: string;
  disabled?: boolean;
  e2eName?: string;
  handleVrmSubmit?: Function;
  hideIntent?: boolean;
  home?: boolean;
  id?: string;
  inputClassName?: string;
  isWidget?: boolean;
  large?: boolean;
  parentKey?: string;
  required?: boolean;
  showLabel?: boolean;
  snowplowEvents?: boolean;
  value?: StickyVrmVersion;
};

type FormValues = {
  vrm: StickyVrmVersion;
}

const VrmChecker = ({
  analyticsEvents,
  buttonProps,
  className,
  disabled,
  e2eName,
  handleVrmSubmit,
  hideIntent,
  home,
  id,
  inputClassName,
  isWidget,
  large,
  required = true,
  showLabel,
  snowplowEvents,
  value,
}: VrmCheckerProps) => {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  const [valueIsQuickVRM, setValueIsQuickVRM] = useState<boolean>(!!value);
  const [html5Validation, setHtml5Validation] = useState<boolean>(true);

  useEffect(() => {
    setHtml5Validation(false);
  }, []);

  const navigateToLookup = (vrm?: FormValues['vrm']) => {
    if (vrm) {
      const url = formatVehicleLookupUrl(vrm);

      if (snowplowEvents) {
        if (isWidget) {
          STICKY_VRM_EVENTS.STICKY_VRM_WIDGET_OLD_CTA_CLICK({
            category: getCategoryForSticky(pathname),
            label: vrm,
            url,
          });
        } else {
          STICKY_VRM_EVENTS.STICKY_VRM_BANNER_OLD_CTA_CLICK({
            category: getCategoryForSticky(pathname),
            label: vrm,
            url,
          });
        }
      }

      history.push(url);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValueIsQuickVRM(e.target.value.toUpperCase() === value);
  };

  const handleChangeCarClick = (form: FormApi<FormValues>) => (e: React.MouseEvent) => {
    e.preventDefault();

    form.change('vrm', '' as FormValues['vrm']);
    setValueIsQuickVRM(false);
  };

  const handleSubmit = ({ vrm }: FormValues, form: FormApi<FormValues>) => {
    const reset = () => {
      form.change('vrm', '' as FormValues['vrm']);
    };

    analyticsEvents?.vrmSubmittedSuccessfully?.();
    if (handleVrmSubmit) {
      return handleVrmSubmit(vrm, reset);
    }
    return navigateToLookup(vrm);
  };

  let {
    className: buttonClassName,
    label,
    labelChildren,
    ...restButtonProps
  } = buttonProps || {};

  label = home ? T.translate('components.vrmChecker.buttonLabelValue') : T.translate('components.vrmChecker.buttonLabel', {
    vehicle: vehicleType(pathname),
  });
  label = (value && valueIsQuickVRM) ? T.translate('components.vrmChecker.loggedIn.buttonLabel') : label;
  label = labelChildren || buttonProps?.label || label;

  const isUlezSticky = getCategoryForSticky(pathname) === 'ulez';

  const onInputFocus = () => {
    if (snowplowEvents) {
      if (isWidget) {
        STICKY_VRM_EVENTS.STICKY_VRM_WIDGET_INPUT_FOCUS({
          category: getCategoryForSticky(pathname),
        });
      } else {
        STICKY_VRM_EVENTS.STICKY_VRM_BANNER_INPUT_FOCUS({
          category: getCategoryForSticky(pathname),
          label: (isUlezSticky ? 'sticky' : 'old') as StickyVrmVersion,
          ...(isUlezSticky && { name: 'vrm_input' }),
        });
      }
    } else {
      analyticsEvents?.onInputFocus?.();
    }
  };

  return (
    <Form<FormValues>
      validateOnBlur
      initialValues={{ vrm: value }}
      onSubmit={handleSubmit}
      render={({
        form, handleSubmit: formHandleSubmit,
      }) => (
        <form
          className={className}
          noValidate={!html5Validation}
          onSubmit={(event) => {
            // Reset the same validate function as we set initally
            // for the side effect that it will re-run validation.
            // We need to do this as react-final-form does not run validation on submit
            // when validateOnBlur is true
            // https://github.com/final-form/final-form/issues/213
            form.setConfig('validate', undefined);
            formHandleSubmit(event);
          }}
        >
          <VRMInput
            onChange={handleInputChange}
            onInputBlur={() => {
              analyticsEvents?.invalidVrmWarningDisplayed?.();
            }}
            onInputFocus={() => onInputFocus()}
            vrmId={id}
            {...{ className: inputClassName, disabled, e2eName, hideIntent, large, required }}
          />
          <div className={buttonClassName}>
            <Button
              {...applyCypressData(cypressIds.buttons.vrmButton) }
              {...restButtonProps}
              fullWidth
              as="button"
              data-e2ename={`${e2eName} button`}
              disabled={disabled}
              icon="arrow"
              label={label}
              onClick={() => {
                analyticsEvents?.onButtonClick?.();
              }}
              showLabel={showLabel}
              size={large ? 'large' : 'medium'}
              type="submit"
            />
          </div>
          {value && valueIsQuickVRM
            ? (
              <span>
                {T.translate('components.vrmChecker.loggedIn.changeCarLink', {
                  changeCarLink: <a href="#changecar" onClick={handleChangeCarClick(form)}>{T.translate('components.vrmChecker.loggedIn.changeCarLinkText')}</a>,
                })}
              </span>
            )
            : null}
        </form>
      )}
    />
  );
};

export default VrmChecker;
